/**
 * GAP-SOFT-DELETE (D24): archive administration (tenant Administrator only).
 * Backend: apps/shared/archive_views.py.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

// resetApiState.js imports this module (via ALL_APIS), so import it lazily to avoid a cycle.
const resetStaleModuleCaches = async (dispatch, queryFulfilled) => {
  try {
    await queryFulfilled;
  } catch {
    return;
  }
  const { resetModuleApiState } = await import('./resetApiState');
  resetModuleApiState(dispatch);
};

const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
  );

export const archiveApi = createApi({
  reducerPath: 'archiveApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['ArchiveBatch'],
  endpoints: (builder) => ({
    getArchiveBatches: builder.query({
      query: (params) => ({ url: '/api/v1/archive/', params: clean(params) }),
      providesTags: ['ArchiveBatch'],
    }),
    getArchiveBatch: builder.query({
      query: (batchId) => ({ url: `/api/v1/archive/${batchId}/` }),
      providesTags: (r, e, id) => [{ type: 'ArchiveBatch', id }],
    }),
    restoreArchiveBatch: builder.mutation({
      query: (batchId) => ({ url: `/api/v1/archive/${batchId}/restore/`, method: 'POST' }),
      invalidatesTags: ['ArchiveBatch'],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        resetStaleModuleCaches(dispatch, queryFulfilled),
    }),
    purgeArchiveBatch: builder.mutation({
      query: ({ batchId, confirm, erasure = false, reason = '' }) => ({
        url: `/api/v1/archive/${batchId}/purge/`,
        method: 'POST',
        body: { confirm, erasure, reason },
      }),
      invalidatesTags: ['ArchiveBatch'],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        resetStaleModuleCaches(dispatch, queryFulfilled),
    }),
  }),
});

export const {
  useGetArchiveBatchesQuery,
  useGetArchiveBatchQuery,
  useRestoreArchiveBatchMutation,
  usePurgeArchiveBatchMutation,
} = archiveApi;
